import { TypographyH4 } from '@sosocio/ui/typography';
import EventBus from 'components/event-bus';
import type {
	NativeUploadReconciliationBatch,
	UploadBatchSelection,
	UploadFailureReason,
	UploadProgressMessage,
	UploadProgressMessageV2,
	UploadProgressStage,
} from 'interfaces/app';
import * as PI from 'interfaces/project';
import { nanoid } from 'nanoid';
import * as DialogService from 'services/dialog';
import {
	AppStateModule,
	ConfigModule,
	PhotosModule,
	ProductStateModule,
	UIModule,
	UserModule,
} from 'store';
import { componentSlot } from 'store/ui';
import _ from 'underscore';
import {
	type Component,
	computed,
	reactive,
} from 'vue';
import type { Cons } from 'vue-facing-decorator/dist/component';
import {
	applyProgressUpdate,
	buildExternalUploadChannel,
	buildExternalUploadMutation,
	isUploadProgressMessageV2,
	parseNativeUploadReconciliationBatch,
	shouldAcceptUploadProgress,
	shouldOpenProgressDialog,
	type UploadProgressDialogProps,
} from './external-upload-helpers';
import {
	clearExternalUploadRecovery,
	type ExternalUploadRecoveryMetadata,
	type ExternalUploadRecoveryScope,
	loadExternalUploadRecovery,
	persistExternalUploadRecovery,
	recoveryMetadataToProgressMessage,
} from './external-upload-recovery';
import type { UploadSessionMachineActor } from './session';
import {
	type UploadLifecycleProperties,
	UploadLifecycleTracker,
} from './upload-lifecycle';

type AwsAppSync = typeof import('controllers/aws-appsync').default;

let cachedAwsAppSync: AwsAppSync | null = null;

async function loadAwsAppSync(): Promise<AwsAppSync> {
	if (cachedAwsAppSync === null) {
		cachedAwsAppSync = (await import('controllers/aws-appsync')).default;
	}

	return cachedAwsAppSync;
}

/**
 * Report a failed external-upload AppSync mutation to Bugsnag. These boundaries
 * are best-effort peer signalling: a rejection (network, missing credentials,
 * the dynamic import failing) must never escape and strand the progress dialog
 * open, so callers always run their UI continuation regardless. We still
 * surface the failure so silent peer-signalling breakage stays visible.
 */
function notifyExternalUploadError(
	err: unknown,
	event: 'abort' | 'close' | 'upload' | 'complete' | 'fetchPhotos',
): void {
	if (typeof window.glBugsnagClient === 'undefined') {
		return;
	}

	const error = err instanceof Error
		? err
		: new Error(`External upload ${event} mutation failed`);

	window.glBugsnagClient.notify(
		error,
		(reportedEvent) => {
			reportedEvent.severity = 'warning';
			reportedEvent.addMetadata(
				'externalUpload',
				{
					event,
				},
			);
		},
	);
}

/**
 * Reports an invalid or incomplete native reload snapshot without including file
 * names, local identifiers, or URLs in the diagnostic payload.
 *
 * @param err - Reconciliation failure.
 * @param batchId - Optional non-sensitive correlated batch identity.
 */
function notifyNativeUploadReconciliationError(
	err: unknown,
	batchId?: string,
): void {
	if (typeof window.glBugsnagClient === 'undefined') {
		return;
	}

	const error = err instanceof Error
		? err
		: new Error('Native upload reconciliation failed');

	window.glBugsnagClient.notify(
		error,
		(reportedEvent) => {
			reportedEvent.severity = 'warning';
			reportedEvent.addMetadata(
				'nativeUploadReconciliation',
				{
					batchId,
				},
			);
		},
	);
}

function isTerminalUploadProgress(progress: UploadProgressMessageV2): boolean {
	return (
		progress.stage === 'completed'
		|| progress.stage === 'failed'
		|| progress.stage === 'aborted'
	);
}

export type ExternalUploadDialogComponents = {
	uploadProgress: Cons;
	uploadQr: Component;
};

export type ExternalUploadControllerOptions = {
	appVersion?: string;
	/** Returns the live root upload session actor. */
	getSession: () => UploadSessionMachineActor;
	getPlatform?: () => string;
	/** Called when the user cancels via the in-progress dialog. */
	onCancelAll: () => void;
	trackLifecycleEvent?: (properties: UploadLifecycleProperties) => void;
	dialogs: ExternalUploadDialogComponents;
};

export type UploadProgressDialogSource = 'external' | 'publisher';

export type LocalUploadProgressContext = {
	stage: UploadProgressStage;
	transferredCount: number;
};

type UploadProgressDialogState = {
	close: () => void;
	props: UploadProgressDialogProps;
};

const SHARE_PROGRESS_THROTTLE_MS = 200;

/**
 * Owns the external upload dialog stack (QR + progress) and the AppSync mutation
 * traffic that flows when external uploads (e.g. from a phone over QR) push
 * progress events into this session. The upload controller delegates to this
 * class so that the QR/progress dialog state never lives next to the file-pipe
 * machinery.
 */
export class ExternalUploadController {
	private connectionRecoveryActive = false;

	private readonly lifecycleTracker: UploadLifecycleTracker;

	private localBatchId = nanoid();

	private localBatchFailureReported = false;

	private localBatchSelection: UploadBatchSelection | undefined;

	private localSequence = -1;

	private localUploadSessionId = nanoid();

	private get localProgressSource(): UploadProgressMessageV2['source'] {
		return this.localBatchSelection?.source === 'native-picker'
			? 'native-picker'
			: 'external-device';
	}

	private readonly uploadProgressDialogs = new Map<
		UploadProgressDialogSource,
		UploadProgressDialogState
	>();

	/**
	 * Latches when the user manually closes the progress dialog so that a
	 * subsequent `< 100%` progress tick does not reopen it. Cleared at the start
	 * of each fresh session: {@link showQRCode} for external uploads,
	 * {@link startLocalBatch} for local batches, and at session boundaries for
	 * AppSync-driven flows that bypass `showQRCode`.
	 */
	private readonly dismissedProgressDialogSources = new Set<UploadProgressDialogSource>();

	private uploadQRDialogClose: (() => void) | undefined;

	private readonly shareProgressThrottled: ((data: UploadProgressMessage) => void)
		& { cancel: () => void };

	/**
	 * The most recent progress payload handed to {@link shareProgressThrottled},
	 * and the payload the throttle actually forwarded to the peer. When they
	 * diverge a trailing tick is still pending; {@link dispose} flushes it so a
	 * mid-progress teardown does not leave the peer hanging on a stale `< 100%`
	 * (or never-sent `100%`) state.
	 */
	private pendingShareData: UploadProgressMessage | undefined;

	private lastSentShareData: UploadProgressMessage | undefined;

	public constructor(private readonly options: ExternalUploadControllerOptions) {
		this.lifecycleTracker = new UploadLifecycleTracker({
			appVersion: options.appVersion ?? 'unknown',
			getSamplingRate: () => ConfigModule['analytics.amplitude.samplingRate'],
			platform: options.getPlatform ?? (() => window.glPlatform),
			track: (properties) => options.trackLifecycleEvent?.(properties),
		});
		this.shareProgressThrottled = _.throttle(
			(data: UploadProgressMessage) => {
				this.lastSentShareData = data;
				void this.shareProgress(data);
			},
			SHARE_PROGRESS_THROTTLE_MS,
		);
	}

	/**
	 * Clears the dismissal latch at the start of a fresh local upload batch so
	 * a previously dismissed dialog can reopen for the new run. The latch only
	 * tracks intent for the active session; once the user submits a new batch
	 * that intent no longer applies.
	 *
	 * @param batchId Correlation identifier for the new batch.
	 * @param selection Optional normalized picker totals for lifecycle telemetry.
	 */
	public startLocalBatch(
		batchId = nanoid(),
		selection?: UploadBatchSelection,
	): void {
		this.dismissedProgressDialogSources.delete('publisher');
		this.localBatchId = batchId;
		this.localBatchFailureReported = false;
		this.localBatchSelection = selection;
		this.localSequence = -1;
	}

	/**
	 * Converts the first failed publisher item into one correlated batch-level
	 * terminal observation. This uses the normal progress path so the always-kept
	 * lifecycle failure, peer signal, recovery cleanup, and publisher dialog all
	 * agree on the same terminal state.
	 *
	 * @param failureReason - Privacy-safe normalized failure category.
	 * @returns Whether this call published the batch's first terminal failure.
	 */
	public failLocalBatch(failureReason: UploadFailureReason): boolean {
		if (!AppStateModule.uploadOnly || this.localBatchFailureReported) {
			return false;
		}

		const latestProgress = (
			this.pendingShareData
			&& isUploadProgressMessageV2(this.pendingShareData)
			&& this.pendingShareData.batchId === this.localBatchId
		)
			? this.pendingShareData
			: undefined;
		const submittedCount = latestProgress?.submittedCount
			?? this.localBatchSelection?.newCount
			?? 0;

		this.localBatchFailureReported = true;
		this.dismissedProgressDialogSources.add('publisher');
		this.localSequence += 1;
		this.updateProgress(
			{
				batchId: this.localBatchId,
				failureReason,
				photos: latestProgress?.photos,
				processedCount: latestProgress?.processedCount ?? 0,
				...(typeof latestProgress?.progressPercentage === 'number'
					? {
						progressPercentage: latestProgress.progressPercentage,
					}
					: {}),
				sentAt: new Date().toISOString(),
				sequence: this.localSequence,
				source: this.localProgressSource,
				stage: 'failed',
				submittedCount,
				transferredCount: latestProgress?.transferredCount ?? 0,
				uploadSessionId: this.localUploadSessionId,
				version: 2,
			},
			false,
		);
		return true;
	}

	/** Open the QR dialog for users to scan from their phone. */
	public showQRCode(): Promise<void> {
		/*
		 * A fresh external session resets the dismissal latch so a previously
		 * dismissed progress dialog can re-open for the new upload run.
		 */
		this.dismissedProgressDialogSources.delete('external');

		const closeLoaderDialog = DialogService.openLoaderDialog();
		const session = this.options.getSession();

		session.send({
			type: 'session.external.qr.requested',
		});

		return ProductStateModule
			.getQRCodeForExternalUpload()
			.then(({ image, url }) => {
				closeLoaderDialog();

				let closeDialog: (() => void) | undefined = undefined;
				/**
				 * User-initiated abort (close button / click-outside on the QR
				 * dialog). Tears the QR dialog down and reports the abort to the
				 * session machine before notifying the peer with the AppSync `abort`
				 * mutation, so slow peer signalling cannot strand the UI.
				 *
				 * Contrast with {@link closeQRDialog}, which is the
				 * peer-progress-arrived path: when the QR closes only because the
				 * peer is already uploading we must NOT abort it. {@link dispose}
				 * is the third path: programmatic teardown that also aborts.
				 */
				const abortAndCloseDialog = (): void => {
					closeDialog?.();
					this.uploadQRDialogClose = undefined;
					session.send({
						type: 'session.external.qr.aborted',
					});
					void this.abortPeer();
				};

				this.uploadQRDialogClose = () => {
					closeDialog?.();
					this.uploadQRDialogClose = undefined;
				};

				closeDialog = UIModule.openDialog({
					header: componentSlot({
						tag: TypographyH4,
						slot: window.App.router.$t('views.uploadQr.header'),
					}),
					body: componentSlot({
						tag: this.options.dialogs.uploadQr,
						props: {
							image,
							link: url,
						},
					}),
					props: {
						class: 'md:w-115',
						onClose: abortAndCloseDialog,
						'onClick:outside': abortAndCloseDialog,
					},
				});

				session.send({
					type: 'session.external.qr.shown',
				});
			})
			.catch((err) => {
				session.send({
					type: 'session.external.qr.failed',
				});
				closeLoaderDialog();
				DialogService.openErrorDialog({
					body: {
						content: err.message,
					},
				});
			});
	}

	/**
	 * Update the progress dialog state. Called every tick from the upload
	 * controller's internal poll loop, and on every AppSync progress event for
	 * external uploads.
	 *
	 * The `fromExternalUpload` flag distinguishes the two sources: external
	 * progress also drives the session machine's external child; local progress
	 * tears down the QR dialog the first time it arrives.
	 *
	 * @param data Validated or locally trusted progress payload.
	 * @param fromExternalUpload Whether AppSync delivered the payload from a peer.
	 * @param localProgress Optional lifecycle projection backed by the active batch snapshot.
	 */
	public updateProgress(
		data: UploadProgressMessage,
		fromExternalUpload: boolean,
		localProgress?: LocalUploadProgressContext,
	): void {
		let progressData = data;
		const dialogSource: UploadProgressDialogSource | null = fromExternalUpload
			? 'external'
			: AppStateModule.uploadOnly
				? 'publisher'
				: null;

		if (AppStateModule.uploadOnly) {
			progressData = this.correlateLocalProgress(
				data,
				localProgress,
			);
			this.pendingShareData = progressData;
			this.shareProgressThrottled(progressData);
			this.lifecycleTracker.observe(
				progressData,
				this.localBatchSelection,
			);

			if (this.connectionRecoveryActive) {
				this.markExternalConnectionRecovered();
			}

			this.recordProgressForRecovery(progressData);
		} else if (!fromExternalUpload && this.uploadQRDialogClose) {
			this.closeQRDialog();
		}

		if (fromExternalUpload) {
			/*
			 * Clear the dismissal latch when no external session is in flight.
			 * AppSync-driven external uploads can hit updateProgress without ever
			 * going through showQRCode (e.g. the user accepted the QR on a peer
			 * device); a previous session's dismissed latch must not block this
			 * new session's dialog. The external child sits in `idle` or a
			 * `terminal`-tagged state (`completed` / `aborted`) when no session
			 * is in flight — either signals "next progress event is a new
			 * session boundary".
			 */
			/*
			 * Implicit shared contract: this latch-clearing reads the external
			 * child snapshot BEFORE forwarding the progress event, and so does
			 * `forwardExternalEvent` in session.ts (the `startsAfterTerminalSession`
			 * / `submittedCountChanged` batch-cleanup branch) when it independently
			 * re-reads the same snapshot to detect a session boundary. Both rely on
			 * the pre-event snapshot; the order here (read, then send) must not be
			 * flipped, or session.ts would observe a snapshot already advanced by
			 * this same event and the two boundary detections would disagree.
			 */
			const externalSnapshot = this.options.getSession().getSnapshot().context.externalRef.getSnapshot();
			const progressAccepted = shouldAcceptUploadProgress(
				{
					batchId: externalSnapshot.context.batchId,
					sequence: externalSnapshot.context.sequence,
					stage: externalSnapshot.context.stage,
					submittedCount: externalSnapshot.context.submittedCount,
					supersededBatchIdentities: externalSnapshot.context.supersededBatchIdentities,
					uploadSessionId: externalSnapshot.context.uploadSessionId,
				},
				progressData,
			);

			/*
			 * AppSync delivery is at-least-once and may be reordered. Drop stale
			 * messages before either the state machine or reactive dialog props see
			 * them, so the UI cannot move backward independently of actor state.
			 */
			if (!progressAccepted) {
				return;
			}

			if (externalSnapshot.matches('idle') || externalSnapshot.hasTag('terminal')) {
				this.dismissedProgressDialogSources.delete('external');
			}

			this.options.getSession().send({
				type: 'session.external.progress.received',
				data: progressData,
			});
			this.lifecycleTracker.observe(progressData);

			if (this.connectionRecoveryActive) {
				this.markExternalConnectionRecovered();
			}

			this.recordProgressForRecovery(progressData);
		}

		if (
			dialogSource !== null
			&& isUploadProgressMessageV2(progressData)
			&& (
				progressData.stage === 'failed'
				|| progressData.stage === 'aborted'
			)
		) {
			this.closeProgressDialog(dialogSource);

			if (progressData.stage === 'failed') {
				DialogService.openErrorDialog({
					body: {
						content: window.App.router.$t('uploadStatusFailed'),
					},
				});
			}

			return;
		}

		/*
		 * Local desktop progress is not represented by this dialog. In particular,
		 * it must never overwrite progress owned by an external-device session.
		 */
		if (dialogSource === null) {
			return;
		}

		const openDialog = this.uploadProgressDialogs.get(dialogSource);

		if (openDialog) {
			applyProgressUpdate(
				openDialog.props,
				progressData,
			);
			return;
		}

		const shouldOpen = shouldOpenProgressDialog(
			progressData,
			{
				isUploadOnly: AppStateModule.uploadOnly,
				isFromExternalUpload: fromExternalUpload,
				isDialogOpen: this.uploadProgressDialogs.has(dialogSource),
				wasDismissed: this.dismissedProgressDialogSources.has(dialogSource),
			},
		);

		if (shouldOpen) {
			this.openProgressDialog(
				progressData,
				dialogSource,
			);
		}
	}

	/**
	 * Restores an unexpired URL-free recovery marker as a reconnecting dialog.
	 * It intentionally does not advance the actor: only a fresh AppSync/native
	 * reconciliation message may re-establish authoritative upload state. A
	 * reloaded non-native publisher has no surviving upload authority, so its
	 * stale marker is discarded instead of showing reconnecting indefinitely.
	 *
	 * @returns Whether a matching recovery record could open/update a dialog.
	 */
	public restoreExternalUploadProgress(): boolean {
		if (
			AppStateModule.uploadOnly
			&& window.glPlatform !== 'native'
			&& !this.pendingShareData
		) {
			this.clearRecoveryMetadata();
			this.connectionRecoveryActive = false;
			this.closeProgressDialog('publisher');
			return false;
		}

		const recovery = this.loadRecoveryMetadata();

		if (!recovery) {
			return false;
		}

		if (!this.connectionRecoveryActive) {
			this.connectionRecoveryActive = true;
			this.lifecycleTracker.recordRecovery(
				recoveryMetadataToProgressMessage(recovery),
				'recovering',
			);
		}

		const source: UploadProgressDialogSource = AppStateModule.uploadOnly
			? 'publisher'
			: 'external';
		const message = recoveryMetadataToProgressMessage(recovery);
		let dialog = this.uploadProgressDialogs.get(source);

		if (!dialog && this.dismissedProgressDialogSources.has(source)) {
			return false;
		}

		if (!dialog) {
			this.openProgressDialog(
				message,
				source,
			);
			dialog = this.uploadProgressDialogs.get(source);
		}

		if (dialog) {
			dialog.props.stage = 'reconnecting';
		}

		return Boolean(dialog);
	}

	/** Marks the current recovered/active progress dialog as reconnecting. */
	public markExternalConnectionLost(): void {
		const recovery = this.loadRecoveryMetadata();

		if (!recovery) {
			return;
		}

		if (!this.connectionRecoveryActive) {
			this.connectionRecoveryActive = true;
			this.lifecycleTracker.recordRecovery(
				recoveryMetadataToProgressMessage(recovery),
				'recovering',
			);
		}

		const source: UploadProgressDialogSource = AppStateModule.uploadOnly
			? 'publisher'
			: 'external';
		const dialog = this.uploadProgressDialogs.get(source);

		if (dialog) {
			dialog.props.stage = 'reconnecting';
			return;
		}

		this.restoreExternalUploadProgress();
	}

	/** Restores the last confirmed stage after the signalling channel reconnects. */
	public markExternalConnectionRecovered(): void {
		const recovery = this.loadRecoveryMetadata();

		if (!recovery || !this.connectionRecoveryActive) {
			return;
		}

		this.lifecycleTracker.recordRecovery(
			recoveryMetadataToProgressMessage(recovery),
			'recovered',
		);
		this.connectionRecoveryActive = false;

		const source: UploadProgressDialogSource = AppStateModule.uploadOnly
			? 'publisher'
			: 'external';
		const dialog = this.uploadProgressDialogs.get(source);

		if (dialog?.props.stage === 'reconnecting') {
			dialog.props.stage = recovery.stage;
		}
	}

	/**
	 * Reconciles a reloaded WebView with the native shell when the installed app
	 * exposes the v2 bridge method. An empty authoritative result retires stale
	 * client recovery state; older apps simply skip this capability.
	 *
	 * @returns Accepted native batches, or `null` when the bridge is unavailable.
	 */
	public async reconcileNativeUploads(): Promise<NativeUploadReconciliationBatch[] | null> {
		const nativeBridge = window.webToNative;

		if (!nativeBridge?.getActiveUploads) {
			return null;
		}

		const snapshots: unknown = await nativeBridge.getActiveUploads();

		if (!Array.isArray(snapshots)) {
			notifyNativeUploadReconciliationError(
				new Error('Native bridge returned a non-array active-upload response'),
			);
			return [];
		}

		if (snapshots.length === 0) {
			this.clearRecoveryMetadata();
			this.closeProgressDialog('publisher');
			return [];
		}

		const batches: NativeUploadReconciliationBatch[] = [];

		for (const snapshot of snapshots) {
			const batch = parseNativeUploadReconciliationBatch(snapshot);

			if (!batch) {
				notifyNativeUploadReconciliationError(
					new Error('Native bridge returned an invalid active-upload snapshot'),
				);
				return [];
			}

			batches.push(batch);
		}

		/*
		 * The publisher and desktop external-session actor each retain one batch.
		 * Replaying more than one native snapshot would let a later terminal or
		 * active message replace an earlier completed batch before its photos are
		 * imported. Reject the response before any actor/dialog/peer mutation so
		 * the native side keeps authority instead of silently dropping photos.
		 */
		if (batches.length > 1) {
			notifyNativeUploadReconciliationError(
				new Error('Native bridge returned multiple upload batches'),
			);
			return [];
		}

		const reconciledBatches: NativeUploadReconciliationBatch[] = [];

		for (const batch of batches) {
			const { progress } = batch;
			const terminal = isTerminalUploadProgress(progress);

			if (!terminal) {
				try {
					await this.loadNativeCompletedPhotos(batch);
				} catch (error) {
					notifyNativeUploadReconciliationError(
						error,
						progress.batchId,
					);
					continue;
				}

				if (!this.restoreNativeUploadActors(batch)) {
					notifyNativeUploadReconciliationError(
						new Error('Native active-upload snapshot could not rebuild every item actor'),
						progress.batchId,
					);
					continue;
				}

				if (AppStateModule.uploadOnly) {
					this.localBatchId = progress.batchId;
					this.localBatchFailureReported = false;
					this.localBatchSelection = {
						batchId: progress.batchId,
						newCount: progress.submittedCount,
						rejectedCount: 0,
						selectedTotal: progress.submittedCount,
						skippedExistingCount: 0,
						source: progress.source,
					};
					this.localSequence = progress.sequence;
					this.localUploadSessionId = progress.uploadSessionId;
				}
			}

			this.updateProgress(
				progress,
				false,
			);
			reconciledBatches.push(batch);

			if (
				progress.stage === 'completed'
				&& !AppStateModule.uploadOnly
			) {
				await this.addProgressPhotosToProduct(
					progress.photos?.map((photo) => photo.id) ?? [],
				);
			}
		}

		return reconciledBatches;
	}

	/**
	 * Rebuilds native upload item actors from an authoritative bridge snapshot.
	 * Existing local photo models are required so post-transfer processing can
	 * safely resume; a fully transferred item with an uploaded URL starts the actor
	 * directly at processing, while an in-flight item reattaches the native callback
	 * lifecycle. Every item is resolved before the first actor is sent so a rejected
	 * snapshot cannot partially mutate the upload session.
	 *
	 * @param batch - Validated native batch snapshot.
	 * @returns Whether every item actor was submitted for restoration.
	 */
	private restoreNativeUploadActors(batch: NativeUploadReconciliationBatch): boolean {
		const session = this.options.getSession();
		const restorableItems: Array<{
			item: NativeUploadReconciliationBatch['items'][number];
			localPhotoId: string;
			selectedPhoto: PI.PhotoModel | undefined;
		}> = [];

		for (const item of batch.items) {
			const selectedPhoto = ProductStateModule.getPhotosSelected.find((photo) => (
				photo.externalId === item.uploadId
				|| photo.id === item.localPhotoId
			));
			const localPhotoId = item.localPhotoId
				?? (
					typeof selectedPhoto?.id === 'string'
						? selectedPhoto.id
						: undefined
				);

			if (!localPhotoId || !PhotosModule.getById(localPhotoId)) {
				return false;
			}

			restorableItems.push({
				item,
				localPhotoId,
				selectedPhoto,
			});
		}

		restorableItems.forEach(({
			item,
			localPhotoId,
			selectedPhoto,
		}) => {
			session.send({
				type: 'session.item.add',
				item: {
					input: {
						localPhotoId,
						name: item.name,
						totalBytes: item.totalBytes,
						transport: 'native',
						uploadedBytes: item.uploadedBytes,
						uploadId: item.uploadId,
						uploadUrl: item.uploadUrl,
					},
					metadata: {
						height: item.height ?? selectedPhoto?.full_height,
						photoModelId: localPhotoId,
						uploadStart: item.uploadStart,
						url: item.uploadUrl,
						width: item.width ?? selectedPhoto?.full_width,
					},
					transport: 'native',
				},
			});
		});

		return true;
	}

	/**
	 * Loads completed photo models before the publisher resumes its batch poll.
	 * The reconciliation count includes these photos, so accepting a missing model
	 * would leave the poll permanently below its submitted count.
	 *
	 * @param batch - Validated non-terminal native batch snapshot.
	 */
	private async loadNativeCompletedPhotos(batch: NativeUploadReconciliationBatch): Promise<void> {
		const completedPhotos = batch.progress.photos ?? [];
		const missingPhotoIds = completedPhotos
			.filter(({ id }) => !PhotosModule.getById(id))
			.map(({ id }) => id);

		await Promise.all(
			missingPhotoIds.map((id) => PhotosModule.fetchModel({
				id,
			})),
		);

		if (completedPhotos.some(({ id }) => !PhotosModule.getById(id))) {
			throw new Error('Native active-upload snapshot contains an unavailable completed photo');
		}
	}

	/**
	 * Stores a non-terminal v2 message using the active product/user scope.
	 *
	 * @param data Validated progress payload.
	 */
	private recordProgressForRecovery(data: UploadProgressMessage): void {
		if (!isUploadProgressMessageV2(data)) {
			return;
		}

		const scope = this.recoveryScope();
		const storage = this.recoveryStorage();

		if (!scope || !storage) {
			return;
		}

		persistExternalUploadRecovery(
			storage,
			scope,
			data,
		);
	}

	/** @returns Matching unexpired recovery metadata, or `null`. */
	private loadRecoveryMetadata(): ExternalUploadRecoveryMetadata | null {
		const scope = this.recoveryScope();
		const storage = this.recoveryStorage();

		if (!scope || !storage) {
			return null;
		}

		return loadExternalUploadRecovery(
			storage,
			scope,
		);
	}

	/** Removes recovery metadata when native/product state proves it obsolete. */
	private clearRecoveryMetadata(): void {
		const storage = this.recoveryStorage();

		if (storage) {
			clearExternalUploadRecovery(storage);
		}
	}

	/**
	 * Converts an explicit user cancellation into a terminal lifecycle event and
	 * retires the recovery marker so a cancelled batch cannot reappear on reload.
	 */
	private abandonRecoveredBatch(): void {
		const recovery = this.loadRecoveryMetadata();

		if (!recovery) {
			return;
		}

		const abandonedProgress: UploadProgressMessageV2 = {
			...recoveryMetadataToProgressMessage(recovery),
			sentAt: new Date().toISOString(),
			sequence: recovery.sequence + 1,
			stage: 'aborted',
		};
		this.connectionRecoveryActive = false;
		this.updateProgress(
			abandonedProgress,
			false,
		);
		this.clearRecoveryMetadata();
	}

	/** @returns Current product/user recovery scope, or `null` before either exists. */
	private recoveryScope(): ExternalUploadRecoveryScope | null {
		const productId = ProductStateModule.getProductId;
		const userId = UserModule.id;

		if (!productId || !userId) {
			return null;
		}

		return {
			productId: String(productId),
			userId: String(userId),
		};
	}

	/** @returns Session storage when accessible, otherwise `null`. */
	private recoveryStorage(): Storage | null {
		try {
			return window.sessionStorage;
		} catch {
			return null;
		}
	}

	/**
	 * Adds protocol-v2 identity and lifecycle fields to locally generated ticks.
	 * The legacy controller only knows an aggregate percentage and the photos
	 * whose remote thumbnails are ready, so transfer completion is projected as
	 * `saving` until the final photo list proves the batch is complete.
	 *
	 * @param data Locally generated legacy/v2 progress payload.
	 * @param localProgress Stage and exact item count projected from batch-local state.
	 * @returns Correlated protocol-v2 progress payload.
	 */
	private correlateLocalProgress(
		data: UploadProgressMessage,
		localProgress?: LocalUploadProgressContext,
	): UploadProgressMessageV2 {
		if (isUploadProgressMessageV2(data)) {
			return data;
		}

		const stageHint = localProgress?.stage;
		const submittedCount = Math.max(
			0,
			Math.floor(data.submittedCount),
		);
		const progressPercentage = Math.min(
			100,
			Math.max(
				0,
				data.progressPercentage,
			),
		);
		const transferredCount = typeof localProgress?.transferredCount === 'number'
			? Math.min(
				submittedCount,
				Math.max(
					0,
					Math.floor(localProgress.transferredCount),
				),
			)
			: (
				stageHint === 'processing'
				|| stageHint === 'saving'
				|| stageHint === 'completed'
				|| progressPercentage >= 100
			)
				? submittedCount
				: Math.min(
					submittedCount,
					data.photos?.length ?? 0,
				);
		const requestedProcessedCount = (
			stageHint === 'saving'
			|| stageHint === 'completed'
		)
			? submittedCount
			: Math.min(
				submittedCount,
				data.photos?.length ?? 0,
			);
		const processedCount = Math.min(
			transferredCount,
			requestedProcessedCount,
		);
		let stage: UploadProgressStage = stageHint ?? 'transferring';

		if (!stageHint && progressPercentage <= 0) {
			stage = 'selected';
		} else if (
			progressPercentage >= 100
			&& (data.photos?.length ?? 0) >= submittedCount
		) {
			stage = 'completed';
		} else if (!stageHint && progressPercentage >= 100) {
			stage = 'saving';
		}

		this.localSequence += 1;

		return {
			batchId: this.localBatchId,
			photos: data.photos,
			processedCount,
			progressPercentage,
			sentAt: new Date().toISOString(),
			sequence: this.localSequence,
			source: this.localProgressSource,
			stage,
			submittedCount,
			transferredCount,
			uploadSessionId: this.localUploadSessionId,
			version: 2,
		};
	}

	/** Called by the progress dialog's onStartEditing action. */
	public externalUploadsReady(): void {
		const session = this.options.getSession();
		const externalSnapshot = session.getSnapshot().context.externalRef.getSnapshot();
		const photoIds: (number | string)[] = externalSnapshot.context.photos.map(
			(photo) => photo.id,
		);

		session.send({
			type: 'session.external.editing.started',
		});
		void this.addProgressPhotosToProduct(
			photoIds,
			'external',
		);
	}

	/**
	 * Fetches missing remote photo models, drops failed fetches, and adds only
	 * ready ids to the product. Native reload completion and peer completion use
	 * the same path so neither can advertise phantom photos.
	 *
	 * @param photoIds - Remote or already-present photo identifiers.
	 * @param dialogSource - Optional progress-dialog owner to close after import.
	 */
	private async addProgressPhotosToProduct(
		photoIds: (number | string)[],
		dialogSource?: UploadProgressDialogSource,
	): Promise<void> {
		/*
		 * Track the id behind each fetch so we can drop ids whose fetchModel
		 * rejected: an unfetched id would otherwise flow into addPhotos as a
		 * phantom and `external upload completed` would fire as if every photo
		 * had succeeded. Ids already present in the store need no fetch and are
		 * always kept.
		 */
		const fetchedIds: number[] = [];
		const fetchPromises: Promise<PI.PhotoModel>[] = [];

		photoIds.forEach((photoId) => {
			const photoModel = PhotosModule.getById(photoId);

			if (!photoModel && typeof photoId === 'number') {
				fetchedIds.push(photoId);
				fetchPromises.push(PhotosModule.fetchModel({
					id: photoId,
				}));
			}
		});

		const results = await Promise.allSettled(fetchPromises);
		const failedIds = new Set<number>();

		results.forEach((result, index) => {
			if (result.status === 'rejected') {
				const failedId = fetchedIds[index];
				failedIds.add(failedId);
				notifyExternalUploadError(
					result.reason,
					'fetchPhotos',
				);
			}
		});

		const readyIds = photoIds.filter(
			(photoId) => typeof photoId !== 'number' || !failedIds.has(photoId),
		);

		ProductStateModule.addPhotos(readyIds);

		if (dialogSource) {
			this.closeProgressDialog(dialogSource);
		}

		EventBus.emit(
			'external upload completed',
			readyIds,
		);
	}

	/**
	 * Tear down both dialogs as part of programmatic teardown (e.g.
	 * `controller.reset()`). Unlike {@link closeQRDialog} (the
	 * peer-progress-arrived path, which must stay silent so an actively
	 * uploading phone is not interrupted), dispose notifies the peer with the
	 * AppSync `abort` mutation whenever a QR dialog is still open, so the phone
	 * session does not keep uploading into a now-dead controller. Any pending
	 * recoverable publisher batch is retired before its terminal tick is flushed,
	 * so an older native shell cannot restore a locally cancelled batch after a
	 * reload. The pending share is then flushed so peer-facing state is not left
	 * a tick behind before the abort lands.
	 *
	 * Dismissed-source state is NOT cleared here — `dispose()` is invoked from
	 * `controller.reset()` which itself is sometimes triggered by the user
	 * clicking cancel on the progress dialog. Clearing the latch on dispose
	 * would cause a later sub-100% external progress tick to re-open the
	 * dialog the user just dismissed. The latch is cleared explicitly when a
	 * fresh external upload session begins — either via {@link showQRCode} or
	 * detected inside {@link updateProgress} via the external child's state.
	 */
	public dispose(): void {
		this.abandonRecoveredBatch();
		this.flushPendingShare();

		if (this.uploadQRDialogClose) {
			void this.abortPeer();
		}

		this.closeProgressDialog();
		this.closeQRDialog();
	}

	/**
	 * Flush any progress tick the throttle has not yet forwarded. Underscore's
	 * `_.throttle` only exposes `.cancel()` (no lodash-style `.flush()`), so the
	 * pending trailing edge is cancelled and the latest payload is sent
	 * explicitly when it differs from what the throttle last forwarded.
	 */
	private flushPendingShare(): void {
		this.shareProgressThrottled.cancel();

		if (
			this.pendingShareData
			&& this.pendingShareData !== this.lastSentShareData
		) {
			const data = this.pendingShareData;
			this.lastSentShareData = data;
			void this.shareProgress(data);
		}
	}

	/**
	 * Send the AppSync `abort` mutation to the peer (if a channel exists),
	 * notifying that this controller is tearing down so the phone session stops
	 * uploading. A `loadAwsAppSync`/`createMutation` rejection is reported to
	 * Bugsnag rather than escaping as an unhandled rejection — the caller's UI
	 * teardown must still run regardless of whether the peer was reachable.
	 */
	private async abortPeer(): Promise<void> {
		const channel = this.externalUploadChannel();

		if (!channel) {
			return;
		}

		try {
			const appSync = await loadAwsAppSync();
			await appSync.createMutation(buildExternalUploadMutation(
				channel,
				String(ConfigModule['appSync.url']),
				'abort',
				{},
			));
		} catch (err) {
			notifyExternalUploadError(
				err,
				'abort',
			);
		}
	}

	/**
	 * Send the AppSync `close` mutation to the peer (if a channel exists),
	 * signalling that the desktop side has dismissed the progress dialog or
	 * moved on to editing. Like {@link abortPeer}, a rejection is reported to
	 * Bugsnag rather than escaping — the caller's UI continuation
	 * (closeProgressDialog / externalUploadsReady) must always run so the dialog
	 * cannot get stuck open when AppSync is unreachable.
	 */
	private async closePeer(): Promise<void> {
		const channel = this.externalUploadChannel();

		if (!channel) {
			return;
		}

		try {
			const appSync = await loadAwsAppSync();
			await appSync.createMutation(buildExternalUploadMutation(
				channel,
				String(ConfigModule['appSync.url']),
				'close',
				{},
			));
		} catch (err) {
			notifyExternalUploadError(
				err,
				'close',
			);
		}
	}

	/** Close the QR dialog (no AppSync abort). */
	public closeQRDialog(): void {
		this.uploadQRDialogClose?.();
		this.uploadQRDialogClose = undefined;
	}

	/**
	 * Closes one source-owned progress dialog or all dialogs during teardown.
	 *
	 * @param source Optional dialog owner; omitted only for full disposal.
	 */
	public closeProgressDialog(source?: UploadProgressDialogSource): void {
		if (source) {
			const dialog = this.uploadProgressDialogs.get(source);
			dialog?.close();
			this.uploadProgressDialogs.delete(source);
			return;
		}

		this.uploadProgressDialogs.forEach((dialog) => {
			dialog.close();
		});
		this.uploadProgressDialogs.clear();
	}

	/**
	 * Opens a reactive progress dialog owned exclusively by one update source.
	 *
	 * @param data Progress used to seed reactive dialog props.
	 * @param source Source allowed to update or close the dialog.
	 */
	private openProgressDialog(
		data: UploadProgressMessage,
		source: UploadProgressDialogSource,
	): void {
		/*
		 * Seed through the same sanitize helpers every later tick uses
		 * (applyProgressUpdate) so the initial dialog state can never carry a
		 * raw, untrusted value (e.g. a negative percentage from a hostile peer)
		 * that the next tick would then silently correct.
		 */
		const dialogProps = reactive<UploadProgressDialogProps>({
			progressPercentage: 0,
			stage: 'selected',
			submittedCount: 0,
			photos: [],
			standalone: AppStateModule.uploadOnly,
		});
		applyProgressUpdate(
			dialogProps,
			data,
		);

		const closeDialog = UIModule.openDialog({
			body: componentSlot<Cons>({
				tag: this.options.dialogs.uploadProgress,
				props: computed(() => ({
					...dialogProps,
					onCancel: () => {
						/*
						 * onCancelAll routes through controller.reset → externalUpload.dispose
						 * → closeProgressDialog which already closes this specific dialog via
						 * the stored UIModule handle. Avoid an extra UIModule.closeDialog()
						 * pop that would otherwise dismiss whatever dialog now sits on top.
						 */
						this.dismissedProgressDialogSources.add(source);
						this.abandonRecoveredBatch();
						this.options.onCancelAll();
					},
					onClose: () => {
						this.dismissedProgressDialogSources.add(source);
						this.closeProgressDialog(source);
						void this.closePeer();
					},
					onStartEditing: () => {
						this.externalUploadsReady();
						void this.closePeer();
					},
				})),
			}),
			props: {
				class: 'md:w-180',
				hasCloseButton: false,
			},
		});

		this.uploadProgressDialogs.set(
			source,
			{
				close: closeDialog,
				props: dialogProps,
			},
		);
	}

	/**
	 * Publishes progress to the paired peer and emits completion only for a
	 * terminal protocol stage (or the legacy 100% compatibility signal).
	 *
	 * @param data Correlated or legacy peer payload.
	 */
	private async shareProgress(data: UploadProgressMessage): Promise<void> {
		const channel = this.externalUploadChannel();

		if (!channel) {
			return;
		}

		const url = String(ConfigModule['appSync.url']);

		try {
			const appSync = await loadAwsAppSync();

			await appSync.createMutation(buildExternalUploadMutation(
				channel,
				url,
				'upload',
				data,
			));

			const isComplete = isUploadProgressMessageV2(data)
				? data.stage === 'completed'
				: data.progressPercentage >= 100;

			if (isComplete) {
				await appSync.createMutation(buildExternalUploadMutation(
					channel,
					url,
					'complete',
					data,
				));
			}
		} catch (err) {
			/*
			 * Progress sharing is best-effort peer signalling driven from a
			 * fire-and-forget throttle; a rejection here has no UI continuation
			 * to protect but must not surface as an unhandled rejection.
			 */
			notifyExternalUploadError(
				err,
				(
					isUploadProgressMessageV2(data)
					&& data.stage === 'completed'
				) || (
					!isUploadProgressMessageV2(data)
					&& data.progressPercentage >= 100
				)
					? 'complete'
					: 'upload',
			);
		}
	}

	/** @returns Active product/user AppSync channel, or `null` before scope exists. */
	private externalUploadChannel(): string | null {
		const productId = ProductStateModule.getProductId;
		const userId = UserModule.id;

		if (!productId || !userId) {
			return null;
		}

		return buildExternalUploadChannel(
			productId,
			userId,
		);
	}
}
